import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmPurposeClient, FirearmPurposeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmPurposeClient = () => new FirearmPurposeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns
export const useFirearmPurposes = () =>
  useQuery({
    queryKey: ['firearmPurposes'],
    queryFn: async () => {
      const response = await getFirearmPurposeClient().searchFirearmPurposes({
        filters: {},
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmPurposeSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmPurposeClient().searchFirearmPurposes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmPurposeById = (id: string) =>
  useQuery({
    queryKey: ['firearmPurpose', id],
    queryFn: async () => {
      const response = await getFirearmPurposeClient().getFirearmPurposeById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmPurpose = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmPurposeDTO) => {
      await getFirearmPurposeClient().createFirearmPurpose(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmPurposes'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmPurpose = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmPurposeDTO }) => {
      await getFirearmPurposeClient().updateFirearmPurpose(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmPurposes'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmPurpose = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmPurposeClient().deleteFirearmPurpose(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmPurposes'] });
    },
  });
};